/**
 * TeamPresenceWidget — Dashboard tile showing who's online + quick chat access.
 *
 * Role-universal (every user sees this). Features:
 *   - "X online" live count pill
 *   - User list with status dots (green/yellow/red/gray)
 *   - Click user → deep-link to #dm in the Workspace Live Chat (existing room system)
 *   - "Open full chat" CTA → /workspace?tab=chat
 *   - Manual status toggle: Active / Busy / Away for the current user
 *
 * Heartbeats every 60s while tab is active. Polls presence every 45s.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { MessageCircle, Users2, Circle, ChevronDown, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  active:  { color: '#34C759', label: 'Online',  dot: 'bg-[#34C759]' },
  idle:    { color: '#FFB340', label: 'Away',    dot: 'bg-[#FFB340]' },
  busy:    { color: '#FF3B30', label: 'Busy',    dot: 'bg-[#FF3B30]' },
  offline: { color: '#9CA3AF', label: 'Offline', dot: 'bg-[#9CA3AF]' },
};

const StatusDot = ({ status, size = 'sm' }) => {
  const meta = STATUS_META[status] || STATUS_META.offline;
  const sz = size === 'sm' ? 'w-2 h-2' : 'w-2.5 h-2.5';
  return <span className={`${sz} rounded-full ${meta.dot} inline-block flex-shrink-0 ring-2 ring-white`} title={meta.label} />;
};

const TeamPresenceWidget = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPresence = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/presence`, { headers });
      setData(r.data);
    } catch {
      // Silent — widget stays hidden on error
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const heartbeat = useCallback(async (statusOverride) => {
    try {
      await axios.post(`${API}/presence/heartbeat`,
        statusOverride ? { status: statusOverride } : {},
        { headers });
    } catch {}
  }, [headers]);

  const setMyStatus = async (status) => {
    try {
      await axios.post(`${API}/presence/status`, { status }, { headers });
      fetchPresence();
    } catch {}
  };

  useEffect(() => {
    if (!token) return;
    heartbeat();
    fetchPresence();
    const hbInt = setInterval(() => heartbeat(), 60000);
    const polInt = setInterval(() => fetchPresence(), 45000);
    // Heartbeat on focus too, so tab-switching users flip green fast
    const onFocus = () => { heartbeat(); fetchPresence(); };
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(hbInt);
      clearInterval(polInt);
      window.removeEventListener('focus', onFocus);
    };
  }, [token, heartbeat, fetchPresence]);

  if (loading || !data) {
    return (
      <Card data-testid="team-presence-widget-loading">
        <CardContent className="p-6 text-xs text-[#9CA3AF]">Loading team presence…</CardContent>
      </Card>
    );
  }

  const othersOnline = data.users.filter(u => u.user_id !== user?.user_id && u.status !== 'offline');
  const preview = data.users.slice(0, 8);  // show up to 8 in the widget

  return (
    <Card className="border-[#E5E7EB]" data-testid="team-presence-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#002FA7]" />
            Team Chat
            <Badge className="bg-[#34C759]/15 text-[#34C759] text-[10px] ml-1" data-testid="online-count-badge">
              <Circle className="w-2 h-2 mr-1 fill-current" />
              {data.online} online
            </Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            {/* My status toggle */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" data-testid="my-status-toggle">
                  <StatusDot status={data.my_status} /> <span className="mx-1.5">{STATUS_META[data.my_status]?.label}</span>
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setMyStatus('active')} data-testid="status-active">
                  <StatusDot status="active" /> <span className="ml-2">Online / Active</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setMyStatus('busy')} data-testid="status-busy">
                  <StatusDot status="busy" /> <span className="ml-2">Busy · Do not disturb</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setMyStatus('away')} data-testid="status-away">
                  <StatusDot status="idle" /> <span className="ml-2">Away</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" variant="outline" onClick={() => navigate('/workspace?tab=chat')} data-testid="open-full-chat-btn">
              Open full chat <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Summary strip */}
        <div className="grid grid-cols-4 gap-2">
          {['active', 'busy', 'idle', 'offline'].map((st) => {
            const count = data.users.filter(u => u.status === st).length;
            const meta = STATUS_META[st];
            return (
              <div key={st} className="rounded-lg border border-[#E5E7EB] px-2 py-1.5 flex items-center gap-2" data-testid={`status-tile-${st}`}>
                <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} />
                <div className="text-[10px] text-[#6B7280] leading-tight">
                  <p className="font-bold text-[#0A0A0A] text-xs">{count}</p>
                  <p>{meta.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* User list */}
        <div className="space-y-1 max-h-[220px] overflow-y-auto">
          {preview.map((u) => {
            const isMe = u.user_id === user?.user_id;
            return (
              <button
                key={u.user_id}
                onClick={() => navigate(`/workspace?tab=chat&dm=${encodeURIComponent(u.email)}`)}
                disabled={isMe}
                className={`w-full flex items-center gap-3 px-2 py-1.5 rounded-md text-left transition-colors ${
                  isMe ? 'bg-[#002FA7]/5 cursor-default' : 'hover:bg-[#F3F4F6] cursor-pointer'
                }`}
                data-testid={`presence-user-${u.user_id}`}
              >
                <StatusDot status={u.status} size="md" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-[#0A0A0A] truncate">
                    {u.name} {isMe && <span className="text-[10px] text-[#002FA7]">(you)</span>}
                  </p>
                  <p className="text-[10px] text-[#9CA3AF] truncate">{u.role || 'member'} · {STATUS_META[u.status]?.label}</p>
                </div>
                {!isMe && u.status !== 'offline' && (
                  <MessageCircle className="w-3.5 h-3.5 text-[#002FA7] opacity-0 group-hover:opacity-100" />
                )}
              </button>
            );
          })}
          {data.users.length > 8 && (
            <div className="pt-1 text-center">
              <Button variant="ghost" size="sm" className="text-[11px] text-[#002FA7] h-auto py-1" onClick={() => navigate('/workspace?tab=chat')}>
                View all {data.users.length} members →
              </Button>
            </div>
          )}
          {othersOnline.length === 0 && (
            <p className="text-[11px] text-[#9CA3AF] text-center py-2" data-testid="no-one-online">No one else is online right now.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default TeamPresenceWidget;
